import { useEffect } from 'react'
import {
  KEEP_ALIVE_HEARTBEAT_MS,
  getKeepAliveTabId,
  releaseSessionKeepAlive,
  renewSessionKeepAlive,
} from '../utils/sessionKeepAlive'

/**
 * Mantiene viva la sesión mientras esta pestaña esté en una pantalla que no se
 * puede dejar vencer (hoy: carga masiva, /shipment/bulk-upload*).
 *
 * Se monta una vez a nivel de app (App.tsx) con `active` derivado de la ruta, y
 * el `LogoutTimer` lee el registro compartido. Ver `utils/sessionKeepAlive` para
 * el porqué del arriendo con vencimiento.
 *
 * El arriendo se suelta al salir de la ruta, al desmontar y en `pagehide`
 * (cerrar pestaña o F5). No se depende SOLO de eso: `pagehide` no dispara si el
 * navegador se cae, y para ese caso está el TTL.
 */
export function useSessionKeepAlive(
  active: boolean,
  heartbeatMs: number = KEEP_ALIVE_HEARTBEAT_MS
): void {
  useEffect(() => {
    const tabId = getKeepAliveTabId()

    if (!active) {
      releaseSessionKeepAlive(tabId)
      return
    }

    const renew = () => renewSessionKeepAlive(tabId)

    renew()
    const intervalId = setInterval(renew, heartbeatMs)

    // Al volver a primer plano se renueva de una: si la pestaña estuvo oculta y
    // el navegador estranguló o congeló sus timers (bfcache incluido), el
    // arriendo puede estar a punto de vencer aunque la pestaña siga viva.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') renew()
    }
    const onPageHide = () => releaseSessionKeepAlive(tabId)

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      releaseSessionKeepAlive(tabId)
    }
  }, [active, heartbeatMs])
}
